import { useEffect, useState } from 'react';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import api from '../services/api';

interface UseDriverReturn {
    startDriverTutorial: () => void;
    pgrExterno: boolean | undefined;
}

// Hook que utiliza o Driver.js
const useDriver = (path: string): UseDriverReturn => {
    const [pgrExterno, setPgrExterno] = useState<boolean>();
    const [username, setUsername] = useState();
    const [nivelAcesso, setNivelAcesso] = useState();

    const fetchData = async () => {
        const { data: user } = await api.get('dados-usuario');
        setUsername(user.nome);
        const nivelAcesso = await api.get('nivel-acesso');
        setNivelAcesso(nivelAcesso.data.nivel);
        const empRes = await api.get('empresas');
        if (Array.isArray(empRes.data) && empRes.data[0].id) {
            const res = await api.get(`buscar-pgr-cadastro-externo-por-empresa/${empRes.data[0].id}`);
            setPgrExterno(res.data && res.data === 1 ? res.data === 1 : false);
            return true;
        } else {
            return false;
        }
    }

    const driverConfig = () => {
        let steps: DriveStep[];
        if (!pgrExterno) {
            steps = [
                {
                    popover: {
                        title: 'Bem-vindo ao PGR!',
                        description: 'Leia atentamente as instruções do tutorial e siga os passos indicados para gerar o documento corretamente.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    element: '#pgrStep1',
                    popover: {
                        title: 'Tempo para uso',
                        description: 'Esse é o tempo disponível para você utilizar o sistema e gerar o documento. Após o término, seu acesso será bloqueado. Não se preocupe, o processo de criação do documento é simples e direto.',
                        side: "bottom",
                        align: 'center',
                    },
                },
                {
                    element: '#pgrStep2',
                    popover: {
                        title: 'Frente de Trabalho',
                        description: 'Neste momento, você pode escolher para qual frente de trabalho quer gerar o documento, caso tenha mais de uma. Por padrão, a Frente de Trabalho principal será a Matriz.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    element: '#pgrStep3',
                    popover: {
                        title: 'Funções e Riscos',
                        description: 'Insira as funções relacionadas às atividades da Frente de Trabalho da sua empresa.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    element: '#pgrStep4',
                    popover: {
                        title: 'Cronograma de Ação Nesta Revisão Deste PGR',
                        description: 'As rotinas planejadas são criadas automaticamente. Se preferir, você pode adicionar mais rotinas, especificando o nome, o responsável e a data prevista.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    element: '#pgrStep5',
                    popover: {
                        title: 'Finalização de Documento',
                        description: "Após concluir o preenchimento do documento, clique em Finalizar Documento. Isso irá gerar o documento, que será enviado para aprovação do médico responsável. Assim que o médico assinar, você receberá o documento diretamente no seu e-mail!",
                        side: 'top',
                        align: 'center',
                    },
                },
            ];
        } else {
            steps = [
                {
                    popover: {
                        title: 'Bem-vindo ao PGR!',
                        description: 'Leia atentamente as instruções do tutorial e siga os passos indicados para gerar a revisão do documento corretamente.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    element: '#pgrStep1',
                    popover: {
                        title: 'Frente de Trabalho',
                        description: 'Nesta seção, você pode selecionar, editar ou adicionar uma frente de trabalho que será incluída no documento. Certifique-se de que todas as frentes de trabalho relevantes estejam corretamente configuradas para garantir a precisão do documento final.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    element: '#pgrStep2',
                    popover: {
                        title: 'Revisão/Emissão',
                        description: 'Nesta seção, você pode visualizar o histórico das funções incluídas no documento clicando em Histórico. Além disso, é possível solicitar a revisão do documento. Após a solicitação, o documento será enviado para quarentena, onde passará por verificação e aprovação médica. Assim que o médico aprovar, o documento assinado será enviado diretamente para o seu e-mail.',
                        side: 'top',
                        align: 'center',
                    }
                },
                {
                    popover: {
                        title: 'Funcionalidades bloqueadas',
                        description: 'Para evitar a sobreposição do documento existente e garantir a integridade dos dados, informamos que as funcionalidades de inserção de funções, riscos, exames e cronogramas estão bloqueadas, pois o documento anterior ainda está válido. Para continuar, você pode optar por adquirir uma nova revisão — que será um documento completamente distinto do atual — ou criar um documento para uma nova frente de trabalho da empresa.',
                        side: 'top',
                        align: 'center',
                    }
                }
            ];
        }

        const driverObj = driver({
            showProgress: true,
            animate: true,
            allowClose: true,
            smoothScroll: true,
            nextBtnText: 'Próximo',
            prevBtnText: 'Voltar',
            doneBtnText: 'Finalizar',
            steps
        });

        return driverObj;
    };

    const startDriverTutorial: any = () => {
        const driverObj = driverConfig();
        driverObj.drive();
    };

    useEffect(() => {
        if (path !== '/pgr') return;
        const initDriver = async () => {
            let startDriver;
            startDriver = await fetchData();
            await new Promise(resolve => setTimeout(resolve, 500));
            if (!startDriver) return;
            const response = await api.get('verifica-tutorial-driver');
            if (response.data && nivelAcesso === 2) {
                startDriverTutorial(); 
            }
        };

        initDriver();
    }, [username, nivelAcesso, pgrExterno]);

    return { startDriverTutorial, pgrExterno };
};

export default useDriver;
